import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, clsx, CollapsibleContent, CollapsibleRoot, Icon, Popover, PopoverArrow, PopoverContent, SlotDiv, } from '@a-type/ui';
import { useCallback, useDebugValue, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { proxy, subscribe, useSnapshot } from 'valtio';
import { useEffectOnce } from '../hooks/useEffectOnce.js';
import cls from './onboarding.module.css';
export function createOnboarding(name, steps, startImmediately) {
    var _a;
    const stepUnmounted = {};
    const activeStateStr = typeof window !== 'undefined' ?
        localStorage.getItem(`onboarding-${name}`)
        : null;
    let activeState = startImmediately ? steps[0] : null;
    if (activeStateStr) {
        if (activeStateStr === 'complete')
            activeState = 'complete';
        else
            activeState = (_a = steps.find((step) => step === activeStateStr)) !== null && _a !== void 0 ? _a : null;
    }
    const state = proxy({
        active: activeState,
        stepClaims: {},
    });
    subscribe(state, () => {
        if (typeof window !== 'undefined') {
            if (state.active) {
                localStorage.setItem(`onboarding-${name}`, state.active);
            }
            else {
                localStorage.removeItem(`onboarding-${name}`);
            }
        }
    });
    function useBegin() {
        return useCallback(() => {
            if (state.active === null) {
                state.active = steps[0];
            }
        }, []);
    }
    function useSkip() {
        return useCallback(() => {
            state.active = 'complete';
        }, []);
    }
    function useNext(name) {
        const next = useCallback(() => {
            if (state.active !== name)
                return;
            const index = steps.indexOf(name);
            if (index === steps.length - 1) {
                state.active = 'complete';
            }
            else {
                state.active = steps[index + 1];
            }
        }, [name]);
        return next;
    }
    function useStep(name, disableNextOnUnmount = false) {
        // a unique ID for this invocation of this hook. useful if there
        // are multiple mounted components using this same hook and name.
        const id = useState(() => Math.random().toString(16).slice(2))[0];
        const hasClaim = useSnapshot(state.stepClaims)[name] === id;
        useLayoutEffect(() => {
            if (!state.stepClaims[name]) {
                state.stepClaims[name] = id;
                return () => {
                    delete state.stepClaims[name];
                };
            }
        });
        const active = useSnapshot(state).active;
        const next = useNext(name);
        const isLast = steps.indexOf(name) === steps.length - 1;
        const isOnly = steps.length === 1;
        useEffectOnce(() => {
            if (state.stepClaims[name] !== id) {
                return;
            }
            stepUnmounted[name] = false;
            if (disableNextOnUnmount) {
                return;
            }
            return () => {
                stepUnmounted[name] = true;
                setTimeout(() => {
                    if (stepUnmounted[name]) {
                        next();
                    }
                }, 1000);
            };
        });
        useDebugValue(`Step ${name} is ${active === name && hasClaim ? 'active' : 'inactive'} with id ${id}`);
        return [active === name && hasClaim, next, isLast, isOnly, { id }];
    }
    function useCancel() {
        return useCallback(() => {
            state.active = null;
        }, []);
    }
    return {
        useBegin,
        useSkip,
        useStep,
        useCancel,
        useNext,
        begin: () => {
            if (state.active === null) {
                state.active = steps[0];
            }
        },
        skip: () => {
            state.active = 'complete';
        },
        cancel: () => {
            state.active = null;
        },
    };
}
export function OnboardingBanner({ onboarding, step, children, className, disableNext, style, }) {
    const [show, next, isLast, isOnly, { id }] = onboarding.useStep(step);
    return (_jsx(CollapsibleRoot, { open: show, className: clsx('@mode-leek', 'w-full', className), "data-step-id": id, "data-step-name": step, style: style, children: _jsx(CollapsibleContent, { children: _jsxs(Box, { p: true, surface: "secondary", full: "width", col: true, gap: "sm", rounded: true, children: [_jsx(Box, { col: true, gap: "sm", children: children }), _jsx(Box, { items: "center", justify: "end", gap: "sm", children: !disableNext && (_jsx(Button, { emphasis: "light", onClick: next, children: isLast ?
                                isOnly ?
                                    'Ok'
                                    : 'Finish'
                                : 'Next' })) })] }) }) }));
}
export const OnboardingTooltip = function OnboardingTooltip({ onboarding, step, children, disableNext, content, ignoreOutsideInteraction, }) {
    const [show, next, isLast] = onboarding.useStep(step);
    // delay
    const [delayedOpen, setDelayedOpen] = useState(false);
    useEffect(() => {
        if (show) {
            const timeout = setTimeout(() => {
                setDelayedOpen(true);
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [show]);
    const anchorRef = useRef(null);
    return (_jsxs(Popover, { open: delayedOpen && show, onOpenChange: (open, ev) => {
            if (!open) {
                // if the user interacts outside the popover,
                // and it's with anything besides a button or input,
                // go to the next step
                const ignored = ignoreOutsideInteraction === true ||
                    (typeof ignoreOutsideInteraction === 'function' &&
                        ev.event.target instanceof HTMLElement &&
                        ignoreOutsideInteraction(ev.event.target));
                if (ev.reason === 'outside-press' && ignored) {
                    return;
                }
                next();
            }
        }, modal: false, children: [_jsx(SlotDiv, { render: children, ref: anchorRef }), _jsxs(PopoverContent, { className: clsx('@mode-leek', cls.popover), collisionPadding: 16, anchor: anchorRef, children: [_jsx(PopoverArrow, { className: "!fill-main-wash" }), _jsxs(Box, { items: "center", gap: "sm", children: [content, !disableNext && (_jsx(Button, { emphasis: isLast ? 'primary' : 'ghost', size: "small", onClick: next, children: isLast ? 'Finish' : _jsx(Icon, { name: "x" }) }))] })] })] }));
};
//# sourceMappingURL=onboarding.js.map